import { RedirectLoginOptions } from '@auth0/auth0-react';
import { Auth0FrontendConfig } from './auth0-data.provider';
import { removeAllSessions, removeP2pSession } from './auth0-storage.provider';
import { Auth0Logout, logout } from './auth0.service';

const SILENT_REAUTH_ATTEMPTS_KEY = 'p2p_silent_reauth_attempts';
const MAX_SILENT_REAUTH_ATTEMPTS = 2;
const SILENT_REAUTH_WINDOW_MS = 5 * 60_000;

interface SilentReauthAttempts {
  count: number;
  firstAttemptAt: number;
}

export interface EndSessionRequest {
  auth0Config?: Auth0FrontendConfig;
  auth0Logout: Auth0Logout;
  loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>;
  redirectOptions?: RedirectLoginOptions;
}

/**
 * Silent re-auth redirect while sessionStorage attempts remain, hard logout() once spent.
 * The budget resets ONLY when its 5-minute window expires — never on a successful
 * renewal, bootstrap, or response, which would refill it mid-loop.
 * Rejects when the redirect fails so callers can reset their latches.
 */
export async function endSession({ auth0Config, auth0Logout, loginWithRedirect, redirectOptions }: EndSessionRequest): Promise<void> {
  if (shouldAttemptSilentReauth()) {
    console.warn('[Auth Recovery] Redirecting to sign in for silent re-auth');

    try {
      removeP2pSession();
    } catch (error) {
      console.warn('[Auth Recovery] Failed to clear p2p session', { error: getErrorMessage(error) });
    }

    try {
      await loginWithRedirect(redirectOptions);
    } catch (error) {
      console.error('[Auth Recovery] Redirect to sign in failed', { error: getErrorMessage(error) });
      throw error;
    }

    return;
  }

  console.warn('[Auth Recovery] Silent re-auth attempts exhausted, signing out fully');

  try {
    removeAllSessions();

    if (auth0Config) {
      logout({ auth0Config, auth0Logout });
    } else {
      auth0Logout({ logoutParams: { returnTo: window.location.origin } });
    }
  } catch (error) {
    console.error('[Auth Recovery] Hard sign-out failed', { error: getErrorMessage(error) });
  }
}

// In-memory backstop for contexts where sessionStorage writes fail (private mode):
// bounds the loop within a page lifetime. A successful redirect navigates and resets it.
let inMemoryAttemptCount = 0;
const MAX_IN_MEMORY_ATTEMPTS = 5;

export function shouldAttemptSilentReauth(): boolean {
  inMemoryAttemptCount += 1;

  if (inMemoryAttemptCount > MAX_IN_MEMORY_ATTEMPTS) {
    return false;
  }

  const attempts = readAttempts();
  const now = Date.now();

  if (!attempts || now - attempts.firstAttemptAt > SILENT_REAUTH_WINDOW_MS) {
    writeAttempts({ count: 1, firstAttemptAt: now });
    return true;
  }

  if (attempts.count < MAX_SILENT_REAUTH_ATTEMPTS) {
    writeAttempts({ count: attempts.count + 1, firstAttemptAt: attempts.firstAttemptAt });
    return true;
  }

  return false;
}

function readAttempts(): SilentReauthAttempts | undefined {
  try {
    const raw = sessionStorage.getItem(SILENT_REAUTH_ATTEMPTS_KEY);
    const parsed = raw ? JSON.parse(raw) : undefined;

    return isValidAttempts(parsed) ? parsed : undefined;
  } catch (error) {
    console.warn('[Auth Recovery] Failed to read silent re-auth attempts', { error: getErrorMessage(error) });
    return undefined;
  }
}

function isValidAttempts(value: unknown): value is SilentReauthAttempts {
  const attempts = value as SilentReauthAttempts | undefined;

  return Number.isFinite(attempts?.count)
    && Number.isFinite(attempts?.firstAttemptAt)
    // A future timestamp would pin the window open forever (clock skew / tampering)
    && attempts!.firstAttemptAt <= Date.now();
}

function writeAttempts(attempts: SilentReauthAttempts) {
  try {
    sessionStorage.setItem(SILENT_REAUTH_ATTEMPTS_KEY, JSON.stringify(attempts));
  } catch (error) {
    console.warn('[Auth Recovery] Failed to persist silent re-auth attempts', { error: getErrorMessage(error) });
  }
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
